import { useEffect, useState } from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { TrendingUp, Clock, Calendar, ThumbsUp, ThumbsDown, Minus } from 'lucide-react';
import { getAnalyticsSummary, AnalyticsSummary } from '../lib/analytics-service';

interface AnalyticsViewProps {
  userId: string;
}

export default function AnalyticsView({ userId }: AnalyticsViewProps) {
  const [analytics, setAnalytics] = useState<AnalyticsSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, [userId]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const data = await getAnalyticsSummary(userId);
      setAnalytics(data);
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-[16px] p-8 shadow-sm">
        <div className="flex items-center justify-center h-96">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-[#8CAFA8] border-t-transparent"></div>
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="bg-white rounded-[16px] p-8 shadow-sm">
        <p className="text-center text-[#6B7280]">Unable to load analytics data</p>
      </div>
    );
  }

  const categoryColors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

  const sentimentData = [
    { name: 'Positive', value: analytics.sentiments.positive, color: '#10b981', icon: ThumbsUp },
    { name: 'Neutral', value: analytics.sentiments.neutral, color: '#f59e0b', icon: Minus },
    { name: 'Negative', value: analytics.sentiments.negative, color: '#ef4444', icon: ThumbsDown }
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-[#8CAFA8] to-[#7A9B94] text-white rounded-[16px] p-6 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <Clock className="w-5 h-5" />
            </div>
            <h3 className="font-semibold">Avg Response Time</h3>
          </div>
          <p className="text-3xl font-semibold">{analytics.averageResponseTime} days</p>
          <p className="text-blue-100 text-sm mt-1">Time to complete feedback</p>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 text-white rounded-[16px] p-6 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <Calendar className="w-5 h-5" />
            </div>
            <h3 className="font-semibold">Peak Activity</h3>
          </div>
          <p className="text-3xl font-semibold">{analytics.peakActivityDay}</p>
          <p className="text-green-100 text-sm mt-1">Most active day of week</p>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-[16px] p-6 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <TrendingUp className="w-5 h-5" />
            </div>
            <h3 className="font-semibold">Overall Sentiment</h3>
          </div>
          <p className="text-3xl font-semibold">{analytics.sentiments.positive}%</p>
          <p className="text-purple-100 text-sm mt-1">Positive feedback received</p>
        </div>
      </div>

      <div className="bg-white rounded-[16px] p-6 shadow-sm border border-gray-200">
        <div className="flex items-center gap-2 mb-6">
          <TrendingUp className="w-5 h-5 text-[#8CAFA8]" />
          <h3 className="text-lg font-semibold text-[#1F2937]">Response Trends (Last 6 Months)</h3>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={analytics.responseTrends}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="month"
              tick={{ fontSize: 12 }}
              stroke="#9ca3af"
            />
            <YAxis stroke="#9ca3af" />
            <Tooltip
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
              }}
            />
            <Legend />
            <Line
              type="monotone"
              dataKey="requests"
              stroke="#3b82f6"
              strokeWidth={3}
              name="Total Requests"
              dot={{ fill: '#3b82f6', r: 5 }}
              activeDot={{ r: 7 }}
            />
            <Line
              type="monotone"
              dataKey="completed"
              stroke="#10b981"
              strokeWidth={3}
              name="Completed"
              dot={{ fill: '#10b981', r: 5 }}
              activeDot={{ r: 7 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-[16px] p-6 shadow-sm border border-gray-200">
          <h4 className="font-semibold text-[#1F2937] mb-6">Feedback Categories</h4>
          {analytics.categories.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={analytics.categories} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" stroke="#9ca3af" />
                  <YAxis
                    dataKey="category"
                    type="category"
                    tick={{ fontSize: 12 }}
                    width={120}
                    stroke="#9ca3af"
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                    {analytics.categories.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={categoryColors[index % categoryColors.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <div className="mt-4 space-y-2">
                {analytics.categories.map((cat, index) => (
                  <div key={index} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: categoryColors[index % categoryColors.length] }}
                      />
                      <span className="text-[#4B5563]">{cat.category}</span>
                    </div>
                    <span className="font-semibold text-[#1F2937]">{cat.count} ({cat.percentage}%)</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="h-64 flex items-center justify-center text-[#9CA3AF]">
              No category data available yet
            </div>
          )}
        </div>

        <div className="bg-white rounded-[16px] p-6 shadow-sm border border-gray-200">
          <h4 className="font-semibold text-[#1F2937] mb-6">Sentiment Analysis</h4>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={sentimentData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, value }) => `${name}: ${value}%`}
                outerRadius={90}
                fill="#8884d8"
                dataKey="value"
              >
                {sentimentData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px'
                }}
                formatter={(value) => `${value}%`}
              />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-4 space-y-3">
            {sentimentData.map((item, index) => {
              const Icon = item.icon;
              return (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div
                      className="p-2 rounded-lg"
                      style={{ backgroundColor: `${item.color}20` }}
                    >
                      <Icon className="w-4 h-4" style={{ color: item.color }} />
                    </div>
                    <span className="font-medium text-[#4B5563]">{item.name}</span>
                  </div>
                  <span className="text-2xl font-semibold" style={{ color: item.color }}>
                    {item.value}%
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-[16px] p-6 shadow-sm border border-gray-200">
        <h4 className="font-semibold text-[#1F2937] mb-4">Monthly Performance Overview</h4>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 text-sm font-semibold text-[#4B5563]">Month</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-[#4B5563]">Requests</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-[#4B5563]">Completed</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-[#4B5563]">Response Rate</th>
              </tr>
            </thead>
            <tbody>
              {analytics.responseTrends.map((trend, index) => (
                <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-4 text-sm text-[#1F2937]">{trend.month}</td>
                  <td className="py-3 px-4 text-sm text-right text-[#4B5563]">{trend.requests}</td>
                  <td className="py-3 px-4 text-sm text-right text-[#4B5563]">{trend.completed}</td>
                  <td className="py-3 px-4 text-sm text-right">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      trend.responseRate >= 80 ? 'bg-green-100 text-green-800' :
                      trend.responseRate >= 60 ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {trend.responseRate}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
